import React, { useState } from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import { useCreateRemoveRetailPriceRangeFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useFieldLevel } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';

// Rating options with fieldLevel mask
const ratingOptionsTemplate: SlDropdownMenuOption[] = [
  { id: 'weekly', label: 'Weekly Rating', fieldName: '{{fieldLevel}}WeeklyRatingVolume' },
  { id: 'cumulative', label: 'Cumulative Rating', fieldName: '{{fieldLevel}}LifetimeRatingVolume' }
];

// Function to apply fieldLevel mask to rating options
const applyFieldLevelMask = (options: SlDropdownMenuOption[], fieldLevel: string): SlDropdownMenuOption[] => {
  return options.map((option) => ({
    ...option,
    fieldName: option.fieldName.replace('{{fieldLevel}}', fieldLevel)
  }));
};

/**
 * Weekly Rating Volume chart grouped by weekId. - if it is not a full year, we will disable the comparison data
 *
 */
const ReviewWeeklyRatingVolumeChart = () => {
  const [selectedRatingType, setSelectedRatingType] = useState<string>('cumulative');
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  const fieldLevel = useFieldLevel();

  // Apply the field level mask to get the actual rating options
  const ratingOptions = applyFieldLevelMask(ratingOptionsTemplate, fieldLevel);

  const selectedOption = ratingOptions.find((option) => option.id === selectedRatingType);
  const fieldName = selectedOption?.fieldName || `${fieldLevel}WeeklyRatingVolume`;

  const handleRatingTypeChange = (option: SlDropdownMenuOption) => {
    setSelectedRatingType(option.id);
  };

  return (
    <BeaconComparableSplineChart
      beaconChartWithLegendOverride={(props) => {
        return {
          ...props,
          includeSummaryInfo: false,
          showTitleSummary: false,
          subtitle: (
            <BeaconSubtitleContainer>
              <Text variant="body1">
                <Flex component="span" alignItems="center" gap={4}>
                  <SlDropdownMenu
                    options={ratingOptions}
                    selectedId={selectedRatingType}
                    onChange={handleRatingTypeChange}
                    buttonVariant="title"
                  />{' '}
                  <Text variant="h4">Volume</Text>
                </Flex>
                {`The chart displays the ${ratingOptions
                  .find((option) => option.id === selectedRatingType)
                  ?.label.toLowerCase()} volume for ${entityPhrase} `}
                {taxonomyText}
                {` during the ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
                <SubtitleFilters />
              </Text>
            </BeaconSubtitleContainer>
          )
        };
      }}
      indexName={BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2}
      fieldName={fieldName}
      requestBuilder={removeRetailPriceRangeFilters()}
    />
  );
};

export default ReviewWeeklyRatingVolumeChart;
